import { useActionSignatureContext } from "app/features/actionSignature/providers/ActionSignatureProvider";
import { useResidentContext } from "app/features/residents/provider/ResidentProvider";
import { useCreateFluidIntakeMutation } from "app/services/api/staticFormsApi";
import { FluidIntakeEnums } from "app/services/api/types/forms/enums/FormEnums";
import { FluidIntakeFormFields } from "app/services/api/types/forms/requests/FluidIntakeFormRequest";
import { FluidIntakeRequest } from "app/services/api/types/forms/requests/FluidIntakeRequest";
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import usePagination from "ui/molecules/pagination/usePagination";
import { useGetResidentQuery } from "../../../services/api/residentsApi";
import { useFluidIntakeWithOffline } from "../../offline/hooks/useFluidIntakeWithOffline";

interface Props {
  onSuccess?: () => void;
}

export function useHandleFluidIntake({ onSuccess }: Props) {
  const { residentId } = useResidentContext();
  const { showNewToast, showErrorToast, showOfflineToast } = useDisplayToast();
  const { showActionSignatureModal } = useActionSignatureContext();
  const [triggerCreateFluidIntake] = useCreateFluidIntakeMutation();

  const { currentPage, handlePageChange } = usePagination();

  const {
    data: history,
    isFetching: isHistoryLoading,
    meta,
  } = useFluidIntakeWithOffline({
    residentId,
    currentPage,
    handlePageChange,
  });

  const { data: residentData, refetch } = useGetResidentQuery(
    { resident: residentId! },
    { skip: !residentId },
  );

  const fluidIntakeTotal = residentData?.fluid_intake_totals ?? 0;

  const today = new Date();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = useForm<FluidIntakeFormFields>({
    defaultValues: {
      residentId,
      fluid_type: "",
      consumed_amount: null,
      intake_method: undefined,
      other_intake_method_details: "",
      additional_information: null,
      recorded_date: format(today, "yyyy-MM-dd"),
      recorded_time: format(today, "HH:mm"),
      password: "",
    },
  });

  const submit = handleSubmit(async (data) => {
    try {
      const response = await showActionSignatureModal({
        title: "Create Fluid Intake Log",
        description: "Please enter your password to create this log",
      });

      const {
        recorded_date: RecordedDate,
        recorded_time: RecordedTime,
        intake_method: IntakeMethod,
        ...rest
      } = data;

      const finalDateTime = new Date(
        `${format(new Date(RecordedDate), "yyyy-MM-dd")}T${format(
          new Date(`1970-01-01T${RecordedTime}`),
          "HH:mm:ss",
        )}`,
      );

      const formattedRecordedAt = format(
        finalDateTime,
        "yyyy-MM-dd'T'HH:mm:ssXXX",
      );

      const transformedData: FluidIntakeRequest = {
        ...rest,
        fluid_type: data.fluid_type || "Water",
        intake_method: Array.isArray(IntakeMethod)
          ? IntakeMethod[0] || FluidIntakeEnums.ORAL
          : IntakeMethod || FluidIntakeEnums.ORAL,
        recorded_at: formattedRecordedAt,
        password: response.password,
        residentId: residentId!,
      };

      await triggerCreateFluidIntake(transformedData).unwrap();
      await refetch();
      showNewToast({
        title: "Fluid Intake logged successfully",
        message: "The log has been created successfully",
        type: "success",
      });
    } catch (e) {
      showOfflineToast(
        {
          title: "Fluid Intake saved offline",
        },
        () => showErrorToast(e),
      );
    }
    if (onSuccess) onSuccess();
  });

  return {
    control,
    isSubmitting,
    submit,
    isDirty,
    history,
    handlePageChange,
    isHistoryLoading,
    fluidIntakeTotal,
    meta,
  };
}
